import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Box, Typography, Stack, Chip, Card, CardContent, Grid, TextField, IconButton, Divider, Alert, Skeleton, CircularProgress } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { ArrowForwardIos, Person, Phone, Hotel, CheckCircle, DirectionsCar, CalendarMonth, AccessTime, LocationOn, Repeat } from "@mui/icons-material";
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from "@mui/material";

const theme = createTheme({
  direction: "rtl",
  typography: { fontFamily: '"Cairo", "Tajawal", Arial, sans-serif' },
  palette: { primary: { main: "#4183c5" } },
});

const API_BASE = "https://dash.tatabu.io/back-end/public/api";

const SARIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} style={{ verticalAlign: "middle", marginRight: 2 }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1124.14 1256.39">
    <path fill={color} d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z" />
    <path
      fill={color}
      d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z"
    />
  </svg>
);

const initialForm = {
  type: "",
  car_model: "",
  seats_number: "",
  price: "",
  name: "",
  phone: "",
  pickup_location: "",
  neighborhood: "",
  pickup_date: "",
  pickup_hour: "",
  hotel_name: "",
  driver_name: "",
  driver_phone_number: "",
  driver_amount: "",
};

// ─── Info Row ─────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.6}>
    <Typography component="span" sx={{ fontFamily: '"Cairo"', fontSize: "0.78rem", color: "#888" }}>
      {label}
    </Typography>
    <Typography component="span" sx={{ fontFamily: '"Cairo"', fontSize: "0.82rem", fontWeight: 600, color: "#333" }}>
      {value || "—"}
    </Typography>
  </Stack>
);

// ─── Parent Order Summary ─────────────────────────────────────────────────────
const ParentOrderSummary = ({ order }) => (
  <Box sx={{ mb: 3, borderRadius: 3, border: "1.5px solid #e8e0f5", bgcolor: "#faf8ff", overflow: "hidden" }}>
    <Box sx={{ bgcolor: "#4183c5", px: 2, py: 1.2, display: "flex", alignItems: "center", gap: 1 }}>
      <DirectionsCar sx={{ color: "#fff", fontSize: 18 }} />
      <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 700, fontSize: "0.82rem", color: "#fff" }}>بيانات الطلب الأصلي — {order.order_number}</Typography>
    </Box>
    <Box sx={{ p: 2 }}>
      <InfoRow label="الخدمة" value={order.type} />
      <Divider sx={{ my: 0.5 }} />
      <InfoRow label="السيارة" value={order.car_model} />
      <Divider sx={{ my: 0.5 }} />
      <InfoRow label="عدد المقاعد" value={`${order.seats_number} مقاعد`} />
      <Divider sx={{ my: 0.5 }} />
      <InfoRow label="الاسم" value={order.name} />
      <Divider sx={{ my: 0.5 }} />

      <InfoRow
        label="الجوال"
        value={
          <a
            dir="ltr"
            href={`https://wa.me/${order.phone.replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#25D366", fontWeight: 700, fontFamily: '"Cairo"' }}
          >
            {order.phone}
          </a>
        }
      />

      <Divider sx={{ my: 0.5 }} />
      <InfoRow label="مكان الاستقبال" value={order.pickup_location} />
      {order.hotel_name && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <InfoRow label="الفندق" value={order.hotel_name} />
        </>
      )}
      <Divider sx={{ my: 0.5 }} />
      <InfoRow label="الإجمالي" value={order.price} />
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreateReturnTrip() {
  const SERVICE_TYPES = ["توصيل من جدة الى مكة", "توصيل من مكة الى جدة", "توصيل من مكة الى المدينة", "توصيل من جدة الى المدينة"];
  //
  const { id } = useParams(); // parent order ID from URL
  const navigate = useNavigate();

  const [parentOrder, setParentOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  // ── Fetch parent order on mount ──────────────────────────────────────────
  useEffect(() => {
    const fetchParentOrder = async () => {
      try {
        setLoadingOrder(true);
        const res = await fetch(`${API_BASE}/taxi-orders/get-one-order/${id}`);
        const json = await res.json();

        if (!res.ok || !json.success) {
          setFetchError(json.message || "فشل في تحميل بيانات الطلب");
          return;
        }

        const order = json.data;
        setParentOrder(order);

        // Pre-fill shared fields from parent order
        setForm((prev) => ({
          ...prev,
          name: order.name || "",
          phone: order.phone || "",
          pickup_location: "",
          neighborhood: "",
          hotel_name: "",
          car_model: order.car_model || "",
          seats_number: order.seats_number || "",
          type: "", // don't pre-fill type, let user pick
        }));
      } catch {
        setFetchError("فشل الاتصال بالخادم");
      } finally {
        setLoadingOrder(false);
      }
    };

    if (id) fetchParentOrder();
  }, [id]);

  // ── Form helpers ─────────────────────────────────────────────────────────
  const handleFormChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!form.type.trim()) newErrors.type = "نوع الخدمة مطلوب";
    if (!form.car_model.trim()) newErrors.car_model = "موديل السيارة مطلوب";
    if (!form.seats_number) newErrors.seats_number = "عدد المقاعد مطلوب";
    if (!form.price) newErrors.price = "السعر مطلوب";
    if (!form.name.trim() || form.name.trim().length < 3) newErrors.name = "الاسم يجب أن يكون 3 أحرف على الأقل";
    if (!form.phone.trim() || form.phone.trim().length < 9) newErrors.phone = "رقم الجوال غير صحيح";
    if (!form.pickup_location.trim()) newErrors.pickup_location = "مكان الاستقبال مطلوب";
    if (!form.neighborhood.trim()) newErrors.neighborhood = "الحي / العنوان مطلوب";
    if (!form.pickup_date) newErrors.pickup_date = "تاريخ الاستقبال مطلوب";
    if (!form.pickup_hour) newErrors.pickup_hour = "ساعة الوصول مطلوبة";
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch(`${API_BASE}/taxi-orders/save-return-trip/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: form.type.trim(),
          car_model: form.car_model.trim(),
          seats_number: Number(form.seats_number),
          price: Number(form.price),
          name: form.name.trim(),
          phone: form.phone.trim(),
          pickup_location: form.pickup_location.trim(),
          neighborhood: form.neighborhood.trim(),
          pickup_date: form.pickup_date,
          pickup_hour: form.pickup_hour,
          hotel_name: form.hotel_name.trim() || null,
          driver_name: form.driver_name.trim() || null,
          driver_phone_number: form.driver_phone_number.trim() || null,
          driver_amount: form.driver_amount ? Number(form.driver_amount) : null,
        }),
      });

      const json = await res.json();

      if (json.success || res.ok) {
        setCreatedOrder(json.data);
        setSuccess(true);
      } else {
        setSubmitError(json.message || "حدث خطأ أثناء الإرسال");
      }
    } catch {
      setSubmitError("فشل الاتصال بالخادم");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loadingOrder) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box dir="rtl" sx={{ minHeight: "100vh", maxWidth: "750px", margin: "auto" }}>
          <Box sx={{ bgcolor: "#4183c5", px: 2, pt: 3, pb: 4 }}>
            <Skeleton variant="text" width={200} height={36} sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.3)" }} />
          </Box>
          <Box sx={{ px: 2, mt: -2 }}>
            <Box sx={{ bgcolor: "#fff", borderRadius: 4, p: 2.5, boxShadow: "0 4px 24px rgba(98,62,151,0.08)" }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 2 }} />
              ))}
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // ── Fetch error ───────────────────────────────────────────────────────────
  if (fetchError) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box dir="rtl" sx={{ minHeight: "100vh", maxWidth: "750px", margin: "auto", px: 2, pt: 4 }}>
          <Alert severity="error" sx={{ fontFamily: '"Cairo"' }}>
            {fetchError}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (success && createdOrder) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />
        <Box dir="rtl" sx={{ minHeight: "100vh", maxWidth: "750px", margin: "auto" }}>
          <Box sx={{ bgcolor: "#4183c5", px: 2, pt: 3, pb: 4 }}>
            <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 800, fontSize: "1.2rem", color: "#fff", textAlign: "center" }}>تم إنشاء رحلة العودة</Typography>
          </Box>
          <Box sx={{ px: 2, mt: -2 }}>
            <Box sx={{ bgcolor: "#fff", borderRadius: 4, p: 3, boxShadow: "0 4px 24px rgba(98,62,151,0.08)", textAlign: "center" }}>
              <CheckCircle sx={{ fontSize: 64, color: "#4183c5", mb: 2 }} />
              <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 800, fontSize: "1.1rem", color: "#1a1a2e", mb: 0.5 }}>تم إنشاء رحلة العودة بنجاح!</Typography>
              <Typography sx={{ fontFamily: '"Cairo"', fontSize: "0.82rem", color: "#888", mb: 3 }}>
                رقم الطلب: <strong style={{ color: "#4183c5" }}>{createdOrder.order_number}</strong>
              </Typography>

              <Box sx={{ borderRadius: 3, border: "1.5px solid #e8e0f5", bgcolor: "#faf8ff", p: 2, textAlign: "right", mb: 3 }}>
                <InfoRow label="الخدمة" value={createdOrder.type} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="السيارة" value={createdOrder.car_model} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="تاريخ الاستقبال" value={createdOrder.pickup_date?.slice(0, 10)} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="الساعة" value={createdOrder.pickup_hour} />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow
                  label="السعر"
                  value={
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 800, fontSize: "1rem", color: "#4183c5" }}>{createdOrder.price}</Typography>
                      <SARIcon size={14} color="#4183c5" />
                    </Stack>
                  }
                />
              </Box>

              <Button fullWidth variant="contained" onClick={() => navigate(-1)} sx={{ fontFamily: '"Cairo"', fontWeight: 700, borderRadius: 2.5, bgcolor: "#4183c5", py: 1.3 }}>
                العودة للخلف
              </Button>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />

      <Box dir="rtl" sx={{ minHeight: "100vh", maxWidth: "750px", margin: "auto" }}>
        {/* Header */}
        <Box sx={{ bgcolor: "#4183c5", px: 2, pt: 3, pb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 800, fontSize: "1.2rem", color: "#fff", textAlign: "center" }}>إنشاء رحلة عودة</Typography>
          </Stack>
          {parentOrder && (
            <Typography sx={{ fontFamily: '"Cairo"', fontSize: "0.78rem", color: "rgba(255,255,255,0.8)", textAlign: "center", mt: 0.5 }}>
              للطلب رقم: {parentOrder.order_number}
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ px: 2, mt: -2 }}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 4, p: 2.5, boxShadow: "0 4px 24px rgba(98,62,151,0.08)", minHeight: 300 }}>
            {/* Back button */}
            <Stack direction="row" alignItems="center" gap={1} mb={2}>
              <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#4183c5" }}>
                <ArrowForwardIos fontSize="small" />
              </IconButton>
              <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 800, fontSize: "1.05rem", color: "#1a1a2e" }}>تفاصيل رحلة العودة</Typography>
            </Stack>

            {/* Parent order summary */}
            {parentOrder && <ParentOrderSummary order={parentOrder} />}

            {/* ── Trip Details Section ── */}
            <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 700, fontSize: "0.85rem", color: "#4183c5", mb: 1.5, mt: 1 }}>بيانات رحلة العودة</Typography>

            <Stack spacing={2.5}>
              {/* Type */}
              {/* <TextField
                label="نوع الخدمة*"
                value={form.type}
                onChange={(e) => handleFormChange("type", e.target.value)}
                error={!!errors.type}
                helperText={errors.type}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
              /> */}

              {/* Car model + Seats */}
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <TextField
                    label="موديل السيارة*"
                    value={form.car_model}
                    onChange={(e) => handleFormChange("car_model", e.target.value)}
                    error={!!errors.car_model}
                    helperText={errors.car_model}
                    fullWidth
                    size="small"
                    inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                    InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                    FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    dir="ltr"
                    label="المقاعد*"
                    type="number"
                    value={form.seats_number}
                    onChange={(e) => handleFormChange("seats_number", e.target.value)}
                    error={!!errors.seats_number}
                    helperText={errors.seats_number}
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 50, style: { fontFamily: '"Cairo"' } }}
                    InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                    FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
                  />
                </Grid>
              </Grid>

              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel sx={{ fontFamily: '"Cairo"' }}>نوع الخدمة*</InputLabel>
                <Select
                  value={form.type}
                  label="نوع الخدمة*"
                  onChange={(e) => handleFormChange("type", e.target.value)}
                  inputProps={{ dir: "rtl" }}
                  sx={{ fontFamily: '"Cairo"' }}
                  MenuProps={{ PaperProps: { sx: { fontFamily: '"Cairo"' } } }}
                >
                  {SERVICE_TYPES.map((type) => (
                    <MenuItem key={type} value={type} sx={{ fontFamily: '"Cairo"', direction: "rtl" }}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && <FormHelperText sx={{ fontFamily: '"Cairo"', textAlign: "right" }}>{errors.type}</FormHelperText>}
              </FormControl>

              {/* Price */}
              <TextField
                dir="ltr"
                label="السعر*"
                type="number"
                value={form.price}
                onChange={(e) => handleFormChange("price", e.target.value)}
                error={!!errors.price}
                helperText={errors.price}
                fullWidth
                size="small"
                inputProps={{ min: 0, style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
              />

              {/* <Divider /> */}

              {/* ── Customer Section ── */}
              {/* <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 700, fontSize: "0.85rem", color: "#4183c5" }}>بيانات العميل</Typography> */}

              {/* Name */}
              {/* <TextField
                label="الاسم*"
                value={form.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
              /> */}

              {/* Phone */}
              {/* <TextField
                label="رقم الجوال*"
                value={form.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                fullWidth
                size="small"
                placeholder="+9665xxxxxxxx"
                inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
                type="tel"
              /> */}

              {/* <Divider /> */}

              {/* ── Location Section ── */}
              <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 700, fontSize: "0.85rem", color: "#4183c5" }}>بيانات الموقع</Typography>

              {/* Pickup location */}
              <TextField
                label="مكان الاستقبال*"
                value={form.pickup_location}
                onChange={(e) => handleFormChange("pickup_location", e.target.value)}
                error={!!errors.pickup_location}
                helperText={errors.pickup_location}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
              />

              {/* Neighborhood */}
              <TextField
                label="العنوان*"
                value={form.neighborhood}
                onChange={(e) => handleFormChange("neighborhood", e.target.value)}
                error={!!errors.neighborhood}
                helperText={errors.neighborhood}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
              />

              {/* Hotel */}
              <TextField
                label="اسم الفندق (اختياري)"
                value={form.hotel_name}
                onChange={(e) => handleFormChange("hotel_name", e.target.value)}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
              />

              {/* Date + Time */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="تاريخ الاستقبال*"
                    type="date"
                    value={form.pickup_date}
                    onChange={(e) => handleFormChange("pickup_date", e.target.value)}
                    error={!!errors.pickup_date}
                    helperText={errors.pickup_date}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true, style: { fontFamily: '"Cairo"' } }}
                    inputProps={{ style: { fontFamily: '"Cairo"' } }}
                    FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="ساعة الوصول*"
                    type="time"
                    value={form.pickup_hour}
                    onChange={(e) => handleFormChange("pickup_hour", e.target.value)}
                    error={!!errors.pickup_hour}
                    helperText={errors.pickup_hour}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true, style: { fontFamily: '"Cairo"' } }}
                    inputProps={{ style: { fontFamily: '"Cairo"' } }}
                    FormHelperTextProps={{ style: { fontFamily: '"Cairo"', textAlign: "right" } }}
                  />
                </Grid>
              </Grid>

              <Divider />

              {/* ── Driver Section (optional) ── */}
              {/* <Typography sx={{ fontFamily: '"Cairo"', fontWeight: 700, fontSize: "0.85rem", color: "#4183c5" }}>بيانات السائق (اختياري)</Typography>

              <TextField
                label="اسم السائق"
                value={form.driver_name}
                onChange={(e) => handleFormChange("driver_name", e.target.value)}
                fullWidth
                size="small"
                inputProps={{ dir: "rtl", style: { fontFamily: '"Cairo"' } }}
                InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
              />

              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <TextField
                    dir="ltr"
                    label="جوال السائق"
                    value={form.driver_phone_number}
                    onChange={(e) => handleFormChange("driver_phone_number", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ dir: "ltr", style: { fontFamily: '"Cairo"' } }}
                    InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                    type="tel"
                    placeholder="05xxxxxxxx"
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    dir="ltr"
                    label="مبلغ السائق"
                    type="number"
                    value={form.driver_amount}
                    onChange={(e) => handleFormChange("driver_amount", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, style: { fontFamily: '"Cairo"' } }}
                    InputLabelProps={{ style: { fontFamily: '"Cairo"' } }}
                  />
                </Grid>
              </Grid> */}
            </Stack>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2.5, fontFamily: '"Cairo"', fontSize: "0.82rem", "& .MuiAlert-icon": { marginRight: 0, marginLeft: "12px" } }}>
                {submitError}
              </Alert>
            )}

            {/* Submit */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                mt: 3,
                py: 1.4,
                borderRadius: 2.5,
                fontFamily: '"Cairo"',
                fontWeight: 800,
                fontSize: "1rem",
                bgcolor: "#4183c5",
                boxShadow: "0 4px 20px rgba(98,62,151,0.3)",
                color: "#fff",
              }}
            >
              {submitting ? "جاري الإرسال..." : "إنشاء رحلة العودة"}
            </Button>
          </Box>
        </Box>
        <Box sx={{ height: 40 }} />
      </Box>
    </ThemeProvider>
  );
}
